// Dépôt des documents (offre Diagnostic) : nature proposée pour un fichier, libellés d'état, tailles lisibles.
// L'application ne décide pas du type d'un fichier : l'API le reconnaît au contenu et refuse ce qui ne concorde pas.
// La nature proposée ici d'après le nom n'est qu'un choix par défaut, que la personne peut changer.
import type { NatureDepot, PieceDeposee } from '../api/types';

/** Natures de pièce que l'API accepte (paramètre « nature » du dépôt). */
export const NATURES_DEPOT = [
  'document',
  'glpi-json',
  'ad-csv',
  'm365-csv',
  'firewall',
  'cloud-json',
  'tickets-csv',
  'achats-csv',
  'fec-txt',
  'note',
] as const satisfies readonly NatureDepot[];

export const LIBELLE_ETAT_PIECE: Record<PieceDeposee['etat'], string> = {
  recu: 'Reçue, à lire',
  lu: 'Lue',
  refuse: 'Refusée',
};

export const TON_ETAT_PIECE: Record<PieceDeposee['etat'], 'info' | 'ok' | 'grave'> = {
  recu: 'info',
  lu: 'ok',
  refuse: 'grave',
};

export const LIBELLE_CONTENU: Record<PieceDeposee['type_contenu'], string> = {
  pdf: 'PDF',
  docx: 'Word',
  xlsx: 'Excel',
  json: 'JSON',
  texte: 'Texte',
};

/** Nature proposée d'après le nom du fichier (la personne la change si besoin ; l'API vérifie le contenu). */
export function natureProposee(nom: string): NatureDepot {
  const n = nom.toLowerCase();
  if (/\.(pdf|docx|xlsx)$/.test(n)) return 'document';
  if (/fec/.test(n) && /\.txt$/.test(n)) return 'fec-txt';
  if (/glpi/.test(n) && /\.json$/.test(n) && !/ticket/.test(n)) return 'glpi-json';
  if (/ticket/.test(n) && /\.csv$/.test(n)) return 'tickets-csv';
  if (/(achat|contrat)/.test(n) && /\.csv$/.test(n)) return 'achats-csv';
  if (/(entra|m365|office|google|workspace)/.test(n) && /\.csv$/.test(n)) return 'm365-csv';
  if (/(^ad-|annuaire|active.?directory|ordinateurs|utilisateurs)/.test(n) && /\.csv$/.test(n)) return 'ad-csv';
  if (/\.(conf|cfg)$/.test(n) || /(pare-feu|firewall|fw-)/.test(n)) return 'firewall';
  if (/(ovh|azure|aws|cloud|scaleway)/.test(n) && /\.json$/.test(n)) return 'cloud-json';
  return 'note';
}

/** Nature proposée pour l'export d'un logiciel du catalogue (écran « Ajouter une source »). */
export function natureDuLogiciel(logiciel: string): NatureDepot {
  if (logiciel === 'tout-logiciel-comptable-via-le-fec') return 'fec-txt';
  if (logiciel === 'microsoft-365' || logiciel === 'google-workspace' || logiciel === 'okta') return 'm365-csv';
  if (logiciel === 'ovhcloud' || logiciel === 'microsoft-azure') return 'cloud-json';
  if (logiciel === 'sage-x3' || logiciel === 'coupa') return 'achats-csv';
  if (logiciel === 'stormshield-sns' || logiciel === 'palo-alto') return 'firewall';
  if (logiciel === 'itop' || logiciel === 'easyvista' || logiciel === 'servicenow' || logiciel === 'jira-service-management')
    return 'tickets-csv';
  return 'note';
}

/** « 1,2 Mo », « 830 Ko » */
export function tailleLisible(octets: number): string {
  if (octets >= 1024 * 1024) return `${(octets / (1024 * 1024)).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
  if (octets >= 1024) return `${Math.round(octets / 1024).toLocaleString('fr-FR')} Ko`;
  return `${octets} o`;
}
